import { motion } from 'motion/react'
import { useCompactViewport } from './useMediaQuery'

/** Strong in-out curve for wipes — things that travel while visible. */
const WIPE_EASE = [0.76, 0, 0.24, 1] as const

export interface BandRevealProps {
  children: React.ReactNode
  className?: string
}

/**
 * Entrance for full-bleed color bands (gradient CTA, dark sections): the band
 * wipes open vertically as it scrolls into view, once. Content inside keeps
 * its own choreography. Phone-sized viewports render statically, and
 * <MotionConfig reducedMotion="user"> removes the motion for reduced-motion
 * users.
 */
export function BandReveal({ children, className }: BandRevealProps) {
  const compact = useCompactViewport()

  if (compact) {
    return <div className={className}>{children}</div>
  }

  // Direct value props, not variant labels: a labelled parent would orchestrate
  // nested TextReveal/ScrollReveal children and freeze their own reveals.
  return (
    <motion.div
      initial={{ clipPath: 'inset(12% 0% 12% 0%)', opacity: 0.6 }}
      whileInView={{ clipPath: 'inset(0% 0% 0% 0%)', opacity: 1 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.9, ease: WIPE_EASE }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

export default BandReveal
